  'use client';

import { ProfileTypes, TargetViewers } from '@/constants/profileType.constant';
import { useIsNekiAdmin, useIsNonProfitAdmin } from '@/hooks/useUser';
import useUserProfile from '@/hooks/useUserProfile';
import { getNumFollowersText, joinWithCommas } from '@/utils/stringHelpers';
import { cx } from 'cva';
import { includes } from 'lodash-es';
import React, { useState, useEffect } from 'react';
import { BiTargetLock, BiWorld } from 'react-icons/bi';
import { HiStar } from 'react-icons/hi';
import { MdVpnLock } from 'react-icons/md';
import Avatar from '../next-ui/Avatar';
import AppLink from '../next-ui/Link/Link';
import Tooltip from '../next-ui/Tooltip';
import Typography from '../next-ui/Typography';
import PostMenu from './PostMenu';
import TimeSincePost from './TimeSincePost';
import FollowNonProfitButton from '../Buttons/FollowNonProfitButton';
import {
  useUpdateNonProfitFollowers,
  useUpdateUserFollowers,
} from '@/actions/feed.actions';
import OrgType from '@/constants/orgType.constant';
import FollowUserButton from '../Buttons/FollowUserButton';
import PostType, { CampaignType } from '@/constants/postType.constants';

const doMentionNonProfit = (post) => {
  const { company, nonProfit } = post.renderProps || {};
  if (
    post.sourcePostType !== PostType.NewsFeeds &&
    nonProfit &&
    company.id !== nonProfit.company_id &&
    nonProfit.company_id > 0 &&
    !post.renderProps?.isPromotionPost
  ) {
    return !nonProfit.blocked;
  } else {
    return false;
  }
};

function PostAuthorProfile(props) {
  const { post, className, home = false, orgSlug = null } = props;
  const { author, createdOn, targetViewers, targetOrgs } = post;
  const { nonProfit, isPromotionPost } = post.renderProps || {};
  const { name, avatar, slug } = author;

  const ownerOrg = post.renderProps.company;

  const { mutate: updateNonProfitFollowers } = useUpdateNonProfitFollowers();
  const { mutate: updateUserFollowers } = useUpdateUserFollowers();

  const [showIndicatorsAndMenu, setShowIndicatorsAndMenu] = useState(false);

  const mentionNonProfit = doMentionNonProfit(post);
  const isAuthorNonProfit =
    author.type === ProfileTypes.Organization &&
    author.orgType === OrgType.NonProfit;
  const isAuthorIndividual = author.type === ProfileTypes.Individual;
  const showFollowers =
    author.type === ProfileTypes.Organization ? isAuthorNonProfit : true;

  const isP2PFundraiser =
    post.sourcePostData.fundRaiseType === CampaignType.P2PFundRaise;

  const user = useUserProfile();
  const isNonProfitAdmin = useIsNonProfitAdmin();
  const isNekiAdmin = useIsNekiAdmin();
  
  const excludeCompanyFollowing = user?.activeCommunity?.orgType === OrgType.ChangeMakers;

  useEffect(() => {
    if (home) {
      setShowIndicatorsAndMenu(true);
    }
  }, [home]);

  const handleToggleFollowNonProfit = (isFollowing) => {
    updateNonProfitFollowers({
      nonProfitId: nonProfit.id,
      offset: isFollowing ? 1 : -1,
    });
  };

  const handleToggleFollowUser = (isFollowing) => {
    updateUserFollowers({
      userId: author.id,
      offset: isFollowing ? 1 : -1,
    });
  };

  const isVisibleToAll = post.targetViewers === TargetViewers.All;
  const isPostOwnedByOrg = ownerOrg.id === user.activeCommunityId;

  const isTargetted = targetViewers === TargetViewers.SelectedOrg;
  const targettedOrgNames = isTargetted
    ? joinWithCommas(targetOrgs.map((org) => org.name))
    : '';

  const targettedOrgMessage = isNonProfitAdmin
    ? `This post is only visible to ${
        targettedOrgNames || 'selected organizations'
      }`
    : 'This post is visible to your organization';

  // const isPostFeatured =
  //   post.companyId === user?.activeCommunityId
  //     ? post?.isFeatured
  //     : includes(post.publicShares?.featuredBy, user?.activeCommunityId);

  const isPostFeatured = isPostOwnedByOrg
    ? post.isFeatured
    : includes(post.publicShares?.featuredBy, user.activeCommunityId);

  const modifiedSlug = isAuthorIndividual ? slug : orgSlug || slug;

  return (
    <div
      className={cx(
        'flex w-full items-center justify-between gap-2.5 lg:gap-4',
        className,
      )}>
      <div className="flex items-center justify-start gap-2.5 lg:gap-4">
        <Avatar src={avatar} alt={name} size="md" />

        <div className="flex flex-col items-start justify-around leading-snug">
          <div className="flex flex-wrap items-center gap-x-1">
            <AppLink href={modifiedSlug ? `/${modifiedSlug}` : null}>
              <Typography
                variant="h3"
                component="h2"
                className={slug && 'group-hover:text-text-hyperlink'}>
                {name}
              </Typography>
            </AppLink>
            {mentionNonProfit && (
              <>
                <Typography variant="h3" color="textDisabled">
                  supporting
                </Typography>
                <AppLink href={nonProfit.slug ? `/${nonProfit.slug}` : null}>
                  <Typography
                    variant="h3"
                    color="primary"
                    component="h2"
                    className="group-hover:text-text-hyperlink">
                    {nonProfit.name}
                  </Typography>
                </AppLink>
              </>
            )}
          </div>
          {author.designation && (
            <div className="flex items-center gap-2">
              <Typography variant="body2" color="textSecondary">
                {author.designation}{' '}
                {author.companyName && `, ${author.companyName}`}
              </Typography>
            </div>
          )}
          {showFollowers && (
            <div className="flex items-center gap-2">
              {author.numFollowers > 0 && (
                <Typography variant="body2" color="textSecondary">
                  {getNumFollowersText(author.numFollowers)}
                </Typography>
              )}
              {showIndicatorsAndMenu && isAuthorNonProfit && (
                <FollowNonProfitButton
                  nonProfit={nonProfit}
                  size="sm"
                  onToggle={handleToggleFollowNonProfit}
                  // excludeCompanyFollowing={excludeCompanyFollowing}
                />
              )}
              {showIndicatorsAndMenu &&
                isAuthorIndividual &&
                author.canFollow && (
                  <FollowUserButton
                    user={author}
                    size="sm"
                    viewer={user}
                    type="plus"
                    noConfirm
                    onToggle={handleToggleFollowUser}
                  />
                )}
            </div>
          )}
          <div className="flex items-center gap-2">
            {!isPromotionPost && (
              <TimeSincePost variant="body2" createdOn={createdOn} />
            )}

            {showIndicatorsAndMenu && (
              <>
                {isTargetted && (
                  <Tooltip content={targettedOrgMessage}>
                    <BiTargetLock className="h-4 w-4 text-text-secondary" />
                  </Tooltip>
                )}
                {targetViewers === TargetViewers.All && (
                  <Tooltip content="This post is visible to everyone">
                    <BiWorld className="h-4 w-4 text-text-secondary" />
                  </Tooltip>
                )}
                {targetViewers === TargetViewers.MyOrg && (
                  <Tooltip content="This post is visible only to your organization">
                    <MdVpnLock className="h-4 w-4 text-text-secondary" />
                  </Tooltip>
                )}
                {isPostFeatured && (
                  <Tooltip content="This post is featured">
                    <HiStar className="h-4 w-4 text-text-secondary" />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {showIndicatorsAndMenu && <PostMenu post={post} />}
    </div>
  );
}

export default PostAuthorProfile;
